import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Calendar, Clock, MapPin, Video, Phone, Building2, User, RefreshCw, FileText } from 'lucide-react';
import './ConsultantBookings.css';

const DEFAULT_SCHEDULER_URL = 'http://localhost:3100';

const hoursFromNow = (h) => new Date(Date.now() + h * 3600 * 1000).toISOString();
const hoursAgo = (h) => new Date(Date.now() - h * 3600 * 1000).toISOString();

const SAMPLE_BOOKINGS = [
  {
    id: 'sample-b1',
    start_time: hoursFromNow(26),
    end_time: hoursFromNow(26.5),
    duration_minutes: 30,
    consultant_name: 'Priya Sharma',
    consultant_state: 'Texas',
    vendor_name: 'Apex Health Staffing',
    client_name: 'Northstar Medical',
    diologic_coordinator: 'Alex Rivera',
    call_type: 'Video',
    status: 'Confirmed',
    notes: 'Zoom link will be shared 30 minutes before the interview.',
  },
  {
    id: 'sample-b2',
    start_time: hoursFromNow(50),
    end_time: hoursFromNow(51),
    duration_minutes: 60,
    consultant_name: 'Priya Sharma',
    consultant_state: 'Texas',
    vendor_name: 'MedBridge Partners',
    client_name: 'Summit Care Group',
    diologic_coordinator: 'Jordan Lee',
    call_type: 'Audio',
    status: 'Confirmed',
    notes: 'Please keep your phone available; coordinator will dial in.',
  },
  {
    id: 'sample-b3',
    start_time: hoursAgo(48),
    end_time: hoursAgo(47.5),
    duration_minutes: 30,
    consultant_name: 'Priya Sharma',
    consultant_state: 'Texas',
    vendor_name: 'Apex Health Staffing',
    client_name: 'Lakeside Clinic',
    diologic_coordinator: 'Alex Rivera',
    call_type: 'Video',
    status: 'Confirmed',
    notes: 'Completed sample interview.',
  },
];

const isDemoMode = () => {
  try {
    const param = new URLSearchParams(window.location.search).get('demo');
    if (param === '1') window.sessionStorage.setItem('demoMode', '1');
    else if (param === '0' || param === 'off') window.sessionStorage.removeItem('demoMode');
    return window.sessionStorage.getItem('demoMode') === '1';
  } catch {
    return false;
  }
};

const formatWhen = (isoStart, isoEnd, durationMinutes) => {
  if (!isoStart) return { date: '—', time: '—', range: '—' };
  const start = new Date(isoStart);
  const end = isoEnd
    ? new Date(isoEnd)
    : new Date(start.getTime() + (durationMinutes || 30) * 60000);
  const date = start.toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  const opts = { hour: 'numeric', minute: '2-digit' };
  const time = `${start.toLocaleTimeString(undefined, opts)} – ${end.toLocaleTimeString(undefined, opts)}`;
  return { date, time, range: `${date} · ${time}` };
};

/**
 * Read-only booking details for consultants.
 * Admin books in Diologic Scheduler with consultant name → matched here by email/display name.
 */
function ConsultantBookings({ user }) {
  const baseUrl = process.env.REACT_APP_SCHEDULER_URL || DEFAULT_SCHEDULER_URL;
  const email = user?.email || '';
  const displayName = user?.displayName || '';

  const [bookings, setBookings] = useState([]);
  const [usingSample, setUsingSample] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  const applySample = useCallback(() => {
    setBookings(SAMPLE_BOOKINGS);
    setUsingSample(true);
    setError('');
  }, []);

  const load = useCallback(async () => {
    // Preview / demo: always show sample so you can see the UI
    if (isDemoMode() || (!email && !displayName)) {
      applySample();
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const qs = new URLSearchParams();
      if (email) qs.set('email', email);
      if (displayName) qs.set('name', displayName);
      const r = await fetch(`${baseUrl.replace(/\/$/, '')}/api/consultant-bookings?${qs}`);
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || 'Failed to load bookings');
      const list = Array.isArray(data.bookings) ? data.bookings : [];
      if (list.length === 0) {
        applySample();
      } else {
        setBookings(list);
        setUsingSample(false);
      }
    } catch (err) {
      console.error('ConsultantBookings:', err);
      // Fall back to sample so the page is still previewable
      applySample();
    } finally {
      setLoading(false);
    }
  }, [baseUrl, email, displayName, applySample]);

  useEffect(() => {
    load();
  }, [load, reloadKey]);

  const { upcoming, past } = useMemo(() => {
    const now = Date.now();
    const up = [];
    const pa = [];
    bookings.forEach((b) => {
      const t = new Date(b.start_time).getTime();
      if (Number.isFinite(t) && t >= now - 30 * 60 * 1000) up.push(b);
      else pa.push(b);
    });
    up.sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
    pa.sort((a, b) => new Date(b.start_time) - new Date(a.start_time));
    return { upcoming: up, past: pa };
  }, [bookings]);

  return (
    <div className="consultant-bookings">
      <div className="cb-header">
        <div>
          <h1>My Bookings</h1>
          <p>Interview slots booked for you in Diologic Scheduler</p>
        </div>
        <button
          type="button"
          className="cb-refresh"
          onClick={() => setReloadKey((k) => k + 1)}
          disabled={loading}
        >
          <RefreshCw size={16} className={loading ? 'spin' : ''} />
          Refresh
        </button>
      </div>

      {usingSample && !loading && (
        <div className="cb-sample-banner">
          Showing sample bookings for preview — real scheduler bookings replace these when matched to your name.
        </div>
      )}

      {loading && (
        <div className="cb-state">
          <div className="loading-spinner" />
          <p>Loading your bookings…</p>
        </div>
      )}

      {!loading && error && !usingSample && (
        <div className="cb-state cb-error">
          <Calendar size={36} />
          <p>{error}</p>
          <button type="button" className="cb-refresh" onClick={() => setReloadKey((k) => k + 1)}>
            Try again
          </button>
        </div>
      )}

      {!loading && !error && bookings.length === 0 && (
        <div className="cb-state">
          <Calendar size={36} />
          <h3>No bookings yet</h3>
          <p>
            When an admin books a slot with your name
            {displayName ? ` (“${displayName}”)` : ''}
            {email ? ` or matching “${email.split('@')[0]}”` : ''}, it will show up here.
          </p>
        </div>
      )}

      {!loading && upcoming.length > 0 && (
        <section className="cb-section">
          <h2 className="cb-section-title">Upcoming</h2>
          <div className="cb-list">
            {upcoming.map((b) => (
              <BookingCard key={b.id} booking={b} />
            ))}
          </div>
        </section>
      )}

      {!loading && past.length > 0 && (
        <section className="cb-section">
          <h2 className="cb-section-title">Past</h2>
          <div className="cb-list">
            {past.map((b) => (
              <BookingCard key={b.id} booking={b} past />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function BookingCard({ booking, past = false }) {
  const when = formatWhen(booking.start_time, booking.end_time, booking.duration_minutes);
  const CallIcon = booking.call_type === 'Audio' ? Phone : Video;

  return (
    <article className={`cb-card ${past ? 'is-past' : ''}`}>
      <div className="cb-card-top">
        <div className="cb-when">
          <span className="cb-date"><Calendar size={15} /> {when.date}</span>
          <span className="cb-time"><Clock size={15} /> {when.time}</span>
        </div>
        <span className="cb-status">{booking.status}</span>
      </div>

      <div className="cb-grid">
        <div className="cb-field">
          <User size={15} />
          <div>
            <span className="cb-label">Consultant</span>
            <span className="cb-value">{booking.consultant_name || '—'}</span>
          </div>
        </div>
        {booking.consultant_state && (
          <div className="cb-field">
            <MapPin size={15} />
            <div>
              <span className="cb-label">State</span>
              <span className="cb-value">{booking.consultant_state}</span>
            </div>
          </div>
        )}
        <div className="cb-field">
          <Building2 size={15} />
          <div>
            <span className="cb-label">Client</span>
            <span className="cb-value">{booking.client_name || '—'}</span>
          </div>
        </div>
        {booking.vendor_name && (
          <div className="cb-field">
            <Building2 size={15} />
            <div>
              <span className="cb-label">Vendor / Employer</span>
              <span className="cb-value">{booking.vendor_name}</span>
            </div>
          </div>
        )}
        <div className="cb-field">
          <CallIcon size={15} />
          <div>
            <span className="cb-label">Call type</span>
            <span className="cb-value">{booking.call_type || '—'} · {booking.duration_minutes || 30} min</span>
          </div>
        </div>
        {booking.diologic_coordinator && (
          <div className="cb-field">
            <User size={15} />
            <div>
              <span className="cb-label">Coordinator</span>
              <span className="cb-value">{booking.diologic_coordinator}</span>
            </div>
          </div>
        )}
      </div>

      {booking.notes ? (
        <div className="cb-notes">
          <FileText size={14} />
          <span>{booking.notes}</span>
        </div>
      ) : null}
    </article>
  );
}

export default ConsultantBookings;
